"use client";

import React from "react";
import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import Image, { StaticImageData } from "next/image";
import { cn } from "@/lib/utils";
import { useWindowSize } from "@/hooks/use-window-size";

export type Slide = {
  name: string;
  logo: StaticImageData;
};

interface CarouselAwardsProps {
  slides: Slide[];
  className?: string;
  speed?: number;
  startDelay?: number;
  startIndex?: number;
  duration?: number;
}

const CarouselAwards: React.FC<CarouselAwardsProps> = ({
  slides,
  className = "",
  speed = 1.2,
  startDelay = 0,
  startIndex = 0,
  duration = 20,
}) => {
  const size = useWindowSize();

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "center",
      startIndex: startIndex,
      containScroll: false,
      slidesToScroll: 1,
      duration: duration,
      inViewThreshold: 0.6,
    },
    [
      AutoScroll({
        playOnInit: true,
        stopOnInteraction: false,
        stopOnFocusIn: false,
        stopOnMouseEnter: false,
        speed: size === "sm" ? 0.6 : speed,
        startDelay: startDelay,
      }),
    ]
  );

  const handleMouseLeave = () => {
    emblaApi?.plugins().autoScroll.play();
  };

  const handleMouseEnter = () => {
    emblaApi?.plugins().autoScroll.reset();
    emblaApi?.plugins().autoScroll.stop();
  };

  const handleMouseOver = () => {
    emblaApi?.plugins().autoScroll.reset();
    emblaApi?.plugins().autoScroll.stop();
  };

  return (
    <div
      ref={emblaRef}
      className={cn("relative flex w-full overflow-hidden", className)}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onMouseOver={handleMouseOver}
    >
      <div className="relative flex flex-row">
        {slides.map((slide, index) => (
          <Image
            key={`${index}-${slide.name}`}
            className="h-[23.4px] w-[50.96px] object-cover md:h-[46.8px] md:w-[101.92px] lg:h-[90px] lg:w-[196px]"
            src={slide.logo}
            alt={slide.name}
            quality={100}
            width={300}
            height={300}
          />
        ))}
      </div>
    </div>
  );
};

export default CarouselAwards;
